import React, { useState } from "react";
import { Link } from "react-router-dom";
import "./Blog.css";

// Import all blog posts
import * as AIWithoutEmotions from "./posts/AIWithoutEmotions";
import * as FutureOfAILeadership from "./posts/FutureOfAILeadership";
import * as RiseOfAgenticAi from "./posts/RiseOfAgenticAi";
import * as OpenAiDevTools from "./posts/OpenAiDevTools";
import * as AIShortcutTrap from "./posts/AIShortcutTrap";

// Collect all post metadata
const postsData = [
  AIWithoutEmotions.metadata,
  FutureOfAILeadership.metadata,
  RiseOfAgenticAi.metadata,
  OpenAiDevTools.metadata,
  AIShortcutTrap.metadata,
].sort((a, b) => new Date(b.date) - new Date(a.date));

// Get categories from posts
const allCategories = [
  ...new Set(postsData.flatMap((post) => post.categories || [])),
];

function Blog() {
  const [activeCategory, setActiveCategory] = useState("All");
  const featuredPost = postsData[0]; // Most recent post becomes featured
  const remainingPosts = postsData.slice(1);
  const [email, setEmail] = useState("");

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const sanitizeEmail = (email) => {
    return email.replace(/[<>/'";(){}]/g, ""); // Strips dangerous characters
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    let sanitizedEmail = sanitizeEmail(email);

    if (!validateEmail(sanitizedEmail)) {
      alert("Invalid email format. Please enter a valid email.");
      return;
    }

    fetch(
      "https://script.google.com/macros/s/AKfycbwLBUY2t-JeSkfULYXAd3yX-Fsp_r7T-_vyeZKKKQqFSTeFgzeHAgZ1a2O6DV9EdVg/exec",
      {
        method: "POST",
        mode: "no-cors",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: sanitizedEmail }),
      }
    )
      .then(() => {
        alert("Subscribed successfully!");
        setEmail("");
      })
      .catch((error) => {
        console.error("Error:", error);
        alert("Subscription failed, please try again.");
      });
  };

  const filteredPosts =
    activeCategory === "All"
      ? remainingPosts
      : remainingPosts.filter((post) =>
          post.categories?.includes(activeCategory)
        );

  return (
    <div className="blog-page">
      <header className="header about-header">
        <div className="header-content">
          <h1 className="big-text">AgentSamur.ai Blog</h1>
          <p className="small-text">
            Insights on AI, automation, and the future of work.
          </p>
        </div>
      </header>

      {/* Featured Post Section */}
      <section className="featured-post-section">
        <div className="container">
          <div className="featured-post">
            <div className="featured-post-image-container">
              <img
                src={featuredPost.image}
                alt={featuredPost.title}
                className="featured-post-image"
              />
              <div className="featured-badge">Featured</div>
            </div>
            <div className="featured-post-content">
              <div className="post-meta">
                <span className="post-date">
                  {new Date(featuredPost.date).toLocaleDateString("en-US", {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  })}
                </span>
                {featuredPost.readTime && (
                  <span className="read-time">
                    {featuredPost.readTime} min read
                  </span>
                )}
              </div>
              <h2 className="featured-post-title">{featuredPost.title}</h2>
              <p className="featured-post-excerpt">{featuredPost.excerpt}</p>
              <Link
                to={`/blog/${featuredPost.id}`}
                className="featured-read-more"
              >
                Read Article →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Filter */}
      <section className="blog-categories">
        <div className="container">
          <div className="category-tabs">
            <button
              className={`category-tab ${
                activeCategory === "All" ? "active" : ""
              }`}
              onClick={() => setActiveCategory("All")}
            >
              All
            </button>
            {allCategories.map((category) => (
              <button
                key={category}
                className={`category-tab ${
                  activeCategory === category ? "active" : ""
                }`}
                onClick={() => setActiveCategory(category)}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Posts List */}
      <section className="blog-posts-section">
        <div className="container">
          <div className="blog-posts-grid">
            {filteredPosts.map((post) => (
              <div key={post.id} className="blog-post-card">
                <div className="blog-post-image-container">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="blog-post-image"
                  />
                </div>
                <div className="blog-post-content">
                  <div className="post-meta">
                    <span className="post-date">
                      {new Date(post.date).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </span>
                    {post.readTime && (
                      <span className="read-time">
                        • {post.readTime} min read
                      </span>
                    )}
                  </div>
                  <h3 className="blog-post-title">{post.title}</h3>
                  <p className="blog-post-excerpt">{post.excerpt}</p>
                  <Link to={`/blog/${post.id}`} className="blog-read-more">
                    Read More →
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Signup */}
      <section className="newsletter-section">
        <div className="container">
          <div className="newsletter-container">
            <h3>Stay Updated</h3>
            <p>
              Get the latest insights on AI and automation delivered to your
              inbox.
            </p>
            <form className="newsletter-form" onSubmit={handleSubmit}>
              <input
                type="email"
                placeholder="Your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <button type="submit">Subscribe</button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Blog;
